import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';

const PermissionsTab = ({ file, investors, investorNetworks = [], users = [], onBack }) => {
	const [selectedInvestors, setSelectedInvestors] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState('');

	useEffect(() => {
		const fetchPermissions = async () => {
			if (!file) return;
			setLoading(true);
			try {
				if (file.visibility === 'PUBLIC') {
					// If public, visually select everyone
					const allInvestorIds = investors.map(i => i.id || i.userId);
					const allNetworkIds = investorNetworks.map(n => n.id || n.userId);
					const allUserIds = users.map(u => u.id || u.userId);
					setSelectedInvestors([...allInvestorIds, ...allNetworkIds, ...allUserIds]);
				} else {
					const existingpermissions = await adminAPI.getFilePermissions(file.id);
					setSelectedInvestors(existingpermissions || []);
				}
			} catch (err) {
				console.error('Failed to fetch permissions', err);
				setMessage('Failed to load existing permissions');
			} finally {
				setLoading(false);
			}
		};

		fetchPermissions();
	}, [file, investors, investorNetworks, users]);

	const savePermissions = async () => {
		setSaving(true);
		setMessage('');

		try {
			await adminAPI.updateFilePermissions(file.id, selectedInvestors);
			setMessage('Permissions updated successfully');
			setTimeout(() => {
				onBack();
			}, 1500);
		} catch (err) {
			setMessage(err.response?.data?.error || err.response?.data?.message || 'Failed to update permissions');
		} finally {
			setSaving(false);
		}
	};

	const toggleInvestor = (investorId) => {
		if (selectedInvestors.includes(investorId)) {
			setSelectedInvestors(selectedInvestors.filter((id) => id !== investorId));
		} else {
			setSelectedInvestors([...selectedInvestors, investorId]);
		}
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-xl font-semibold text-gray-900">File Permissions</h2>
					<p className="text-sm text-gray-600 mt-1">File: {file?.originalName}</p>
				</div>
				<button
					onClick={onBack}
					className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
				>
					Back to Files
				</button>
			</div>

			{message && (
				<div
					className={`p-4 rounded-md ${message.includes('success')
						? 'bg-green-50 text-green-800'
						: 'bg-red-50 text-red-800'
						}`}
				>
					{message}
				</div>
			)}

			<div className="bg-white shadow rounded-lg p-6">
				{loading ? (
					<div className="flex justify-center py-8">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
					</div>
				) : (
					<div className="space-y-6">
						{/* Investors Section */}
						<div>
							<h3 className="text-lg font-medium text-gray-900 mb-4">Select Investors</h3>
							<div className="space-y-3">
								{investors.map((investor) => {
									const investorId = investor.id || investor.userId;
									if (!investorId) return null;
									return (
										<label
											key={investorId}
											className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${selectedInvestors.includes(investorId)
												? 'border-blue-500 bg-blue-50'
												: 'border-gray-200 hover:bg-gray-50'
												}`}
										>
											<input
												type="checkbox"
												className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
												checked={selectedInvestors.includes(investorId)}
												onChange={() => toggleInvestor(investorId)}
											/>
											<div className="ml-3 flex-1 flex justify-between items-center">
												<div>
													<p className="text-sm font-medium text-gray-900">{investor.name}</p>
													<p className="text-sm text-gray-500">{investor.organizationName}</p>
												</div>
												<span className="text-sm text-gray-500">{investor.email}</span>
											</div>
										</label>
									);
								})}
								{investors.length === 0 && <p className="text-gray-500 italic">No investors found.</p>}
							</div>
						</div>

						{/* Investor Networks Section */}
						<div>
							<h3 className="text-lg font-medium text-gray-900 mb-4">Select Investor Networks</h3>
							<div className="space-y-3">
								{investorNetworks.map((network) => {
									const networkId = network.id || network.userId;
									if (!networkId) return null;
									return (
										<label
											key={networkId}
											className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${selectedInvestors.includes(networkId)
												? 'border-blue-500 bg-blue-50'
												: 'border-gray-200 hover:bg-gray-50'
												}`}
										>
											<input
												type="checkbox"
												className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
												checked={selectedInvestors.includes(networkId)}
												onChange={() => toggleInvestor(networkId)}
											/>
											<div className="ml-3 flex-1 flex justify-between items-center">
												<div>
													<p className="text-sm font-medium text-gray-900">{network.name || network.organizationName}</p>
													<p className="text-sm text-gray-500">{network.email}</p>
												</div>
												<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
													Network
												</span>
											</div>
										</label>
									);
								})}
								{investorNetworks.length === 0 && <p className="text-gray-500 italic">No investor networks found.</p>}
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Users Section */}
			{
				users && users.length > 0 && (
					<div className="bg-white shadow rounded-lg p-6">
						<h3 className="text-lg font-medium text-gray-900 mb-4">Select Users</h3>
						<div className="space-y-3">
							{users.map((user) => {
								const userId = user.id || user.userId;
								if (!userId) return null;
								return (
									<label
										key={userId}
										className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${selectedInvestors.includes(userId)
											? 'border-blue-500 bg-blue-50'
											: 'border-gray-200 hover:bg-gray-50'
											}`}
									>
										<input
											type="checkbox"
											className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
											checked={selectedInvestors.includes(userId)}
											onChange={() => toggleInvestor(userId)}
										/>
										<div className="ml-3 flex-1 flex justify-between items-center">
											<div>
												<p className="text-sm font-medium text-gray-900">{user.name}</p>
												<p className="text-sm text-gray-500">{user.email}</p>
											</div>
										</div>
									</label>
								);
							})}
						</div>
					</div>
				)
			}

			<div className="mt-6 flex justify-end">
				<button
					onClick={savePermissions}
					disabled={saving || loading}
					className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
				>
					{saving ? 'Saving...' : 'Save Permissions'}
				</button>
			</div>
		</div>
	);
};

export default PermissionsTab;
